import "../styles/dashboard/dashboard.css";
import { useNavigate, useParams } from "react-router-dom";
import ProfileAvatar from "./users/UserProfile";
import { useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import Header from "./Header";
import { fetchUploadedFiles } from "./documents/DocsManagement";
import DashboardSidebar from "./DashboardSideBar";
import { useTranslation } from 'react-i18next';
import { useAuth } from "./context/AuthContext";
import Loading from "./Loading";
import { Roles } from "../roles";

const tusApi = process.env.REACT_APP_TUS_SERVER;

export function formatMillionUp(value, decimals = 2) {
    if (value == null || Number.isNaN(Number(value))) return { number: '-', unit: '' };

    const n = Number(value);

    if (Math.abs(n) >= 1_000_000_000) {
        return { number: (n / 1_000_000_000).toFixed(decimals), unit: 'Billion' };
    }

    if (Math.abs(n) >= 1_000_000) {
        return { number: (n / 1_000_000).toFixed(decimals), unit: 'Million' };
    }

    // Even small numbers return object now
    return { number: n.toLocaleString(), unit: '' };
}


export default function Dashboard() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { lang } = useParams();
    const [users, setUsers] = useState([]);
    const { user, role, token, loading } = useAuth();
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const username = user?.userName || "";
    const apiUrl = process.env.REACT_APP_OPT_SERVER;
    const [summary, setSummary] = useState({
        totalUsers: 0,
        totalFiles: 0,
        totalSize: { value: 0, unit: 'B' },
        totalUniqueEntities: 0
    });

    const [stats, setStats] = useState([]);
    const [loadingData, setLoadingData] = useState(false);
    const AVERAGE_KHMER_WORD_LENGTH = 6.9;   // average characters per word
    const AVERAGE_KHMER_TOKEN_LENGTH = 4.3;  // average characters per token
    const TEXT_RATIO = 0.6;                  // estimated ratio of text in files (HTML/DOCX/TXT)
    const isClickable = role === 0 || role === 1;
    const navigateDocManagement = (username) => {
        if (role === Roles.ADMIN || role === Roles.SUPERADMIN) {
            navigate(`/lang/${lang}/admin/doc-manager`, {
                state: { viewedByAdmin: true },
            });
        }
    };


    const navigateUserManagement = (username) => {
        if (role === Roles.ADMIN || role === Roles.SUPERADMIN) {
            navigate(`/lang/${lang}/admin/user-manager`, {
                state: { viewedByAdmin: true },
            });
        }
    };

    const totalSizeBytes = uploadedFiles
        .filter(file => file.completed)  // only include completed files
        .reduce((sum, file) => sum + file.fileSize, 0);

    const totalSizeMB = totalSizeBytes / (1024 * 1024);

    useEffect(() => {
        if (!username || !token) return;

        const fetchData = async () => {
            setLoadingData(true);
            try {
                // Fetch files first
                const files = await fetchUploadedFiles(tusApi, username, token);
                setUploadedFiles(files);

                // Decode username from token
                const decoded = jwtDecode(token);
                const usernameFromToken = decoded?.username;
                if (!usernameFromToken) throw new Error("Username not found in token");

                // Fetch users with fetch()
                const usersRes = await fetch(
                    `${tusApi}/api/uploads/admin-dashboard?userId=${encodeURIComponent(usernameFromToken)}`,
                    {
                        method: "GET",
                        headers: {
                            "Authorization": `Bearer ${token}`,
                            "Content-Type": "application/json"
                        }
                    }
                );

                if (!usersRes.ok) {
                    throw new Error(`HTTP error! status: ${usersRes.status}`);
                }

                const { users: fetchedUsers = [], stats = [] } = await usersRes.json();

                // Merge user data with stats
                const mergedUsers = fetchedUsers.map(user => {
                    const stat = stats.find(s => s.username === user.username) || {};
                    return {
                        ...user,
                        fileCount: stat.fileCount || 0,
                        totalSize: stat.totalSize || 0,
                        totalSizeMB: stat.totalSizeMB || "0.00",
                    };
                });

                setUsers(mergedUsers);

                const formatFileSize = (bytes) => {
                    if (!bytes) return { value: 0, unit: 'B' };

                    if (bytes < 1024) return { value: bytes, unit: 'B' };
                    if (bytes < 1024 * 1024) return { value: +(bytes / 1024).toFixed(1), unit: 'KB' };
                    if (bytes < 1024 * 1024 * 1024) return { value: +(bytes / (1024 * 1024)).toFixed(1), unit: 'MB' };

                    return { value: +(bytes / (1024 * 1024 * 1024)).toFixed(2), unit: 'GB' };
                };

                // Calculate summary
                const totalUsers = mergedUsers.length;
                const totalFiles = mergedUsers.reduce((sum, user) => sum + user.fileCount, 0);
                const totalSizeBytes = mergedUsers.reduce((sum, user) => sum + user.totalSize, 0);

                // Format number & unit
                const totalSize = formatFileSize(totalSizeBytes);
                const uniqueEntities = [...new Set(mergedUsers.map((user) => user.entity))];

                // Count how many users per entity
                // const entityCounts = mergedUsers.reduce((acc, user) => {
                //     acc[user.entity] = (acc[user.entity] || 0) + 1;
                //     return acc;
                // }, {});

                const totalUniqueEntities = uniqueEntities.length;

                setSummary({ totalUsers, totalFiles, totalSize, totalUniqueEntities });
            } catch (err) {
                console.error("Failed to load data:", err.message || err);
            } finally {
                setLoadingData(false);
            }
        };

        fetchData();
    }, [username, token, apiUrl]);

    const formatBytes = (bytes) => {
        if (bytes === 0) return { value: 0, unit: 'B' };

        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        const value = parseFloat((bytes / Math.pow(k, i)).toFixed(2));
        const unit = sizes[i];

        return { value, unit };
    };


    const loadFiles = async () => {
        try {
            const res = await fetch(`${tusApi}/api/uploads/admin-stat`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error('Failed to fetch uploaded files');

            const files = await res.json();

            // Group by createdAt date
            const grouped = files.reduce((acc, file) => {
                const dateObj = new Date(file.fileDate);
                if (isNaN(dateObj)) return acc;
                const date = dateObj.toLocaleDateString();

                if (!acc[date]) acc[date] = { files: [], totalSize: 0 };
                acc[date].files.push(file);
                acc[date].totalSize += file.fileSize || 0;
                return acc;
            }, {});

            // Convert grouped object into array and calculate status per date
            const statsArray = Object.entries(grouped).map(([date, data]) => {
                const allCompleted = data.files.every(
                    f => f.completed === true && !f.quarantine && !f.infected
                );

                return {
                    date,
                    totalFiles: data.files.length,
                    size: formatBytes(data.totalSize),
                    status: allCompleted ? 'done' : 'pending', // or 'Incomplete'
                };
            });

            setStats(statsArray);
        } catch (error) {
            console.error('Error loading files:', error.message);
        }
    };
    console.log(stats)
    useEffect(() => {
        loadFiles();
    }, [username, token]);

    // const khmerMonths = [
    //     "មករា",
    //     "កុម្ភៈ",
    //     "មិនា",
    //     "មេសា",
    //     "ឧសភា",
    //     "មិថុនា",
    //     "កក្កដា",
    //     "សីហា",
    //     "កញ្ញា",
    //     "តុលា",
    //     "វិច្ឆិកា",
    //     "ធ្នូ",
    // ];

    // const formatDateKhmer = (date) => {
    //     const d = new Date(date);
    //     const day = d.getDate();
    //     const month = khmerMonths[d.getMonth()];
    //     const year = d.getFullYear();

    //     let hours = d.getHours();
    //     const minutes = d.getMinutes().toString().padStart(2, "0");
    //     const ampm = hours >= 12 ? "ល្ងាច" : "ព្រឹក";

    //     hours = hours % 12;
    //     if (hours === 0) hours = 12; // convert 0 to 12 for 12-hour format
    //     const hoursStr = hours.toString().padStart(2, "0");

    //     return `${day} ${month} ${year}, ${hoursStr}:${minutes} ${ampm}`;
    // };
    if (loading || loadingData) return <Loading />;

    function sizeObjectToBytes(sizeObj) {
        if (!sizeObj || !sizeObj.unit) return 0;

        const units = { B: 1, KB: 1024, MB: 1024 ** 2, GB: 1024 ** 3, TB: 1024 ** 4 };
        return (sizeObj.value || 0) * (units[sizeObj.unit] || 1);
    }

    const totalBytes = sizeObjectToBytes(summary.totalSize)*0.15;
    const totalWords = Math.round((totalBytes * TEXT_RATIO) / AVERAGE_KHMER_WORD_LENGTH);
    const totalTokens = Math.round((totalBytes * TEXT_RATIO) / AVERAGE_KHMER_TOKEN_LENGTH);

    // const totalWords = Math.round((parseSizeToBytes(summary.totalSize?.value) * TEXT_RATIO) / AVERAGE_KHMER_WORD_LENGTH);
    // const totalTokens = Math.round((parseSizeToBytes(summary.totalSize?.value) * TEXT_RATIO) / AVERAGE_KHMER_TOKEN_LENGTH);
    const formatted_totalTokens = formatMillionUp(totalTokens);
    const formatted_totalWords = formatMillionUp(totalWords);
    return (

        <div className="uploadpage">
            <Header hideHamburger={false} profileHide={true} hideNav={true} />
            <div className="dashboard">
                <DashboardSidebar
                    uploadedFiles={uploadedFiles}
                    totalSizeMB={totalSizeMB}
                />
                <div div className="main-dashboard">
                    <div className="container">
                        <div className="head">
                            <h2>{t('dashboard')}</h2>
                        </div>
                        <div className="contents">
                            <div className="content">
                                <div className="totally">
                                    <div className="overview-container">

                                        <div className="overview">
                                            <div className={`total ${isClickable ? "clickable" : "disabled"}`}
                                                onClick={isClickable ? () => navigateDocManagement() : undefined}
                                            >
                                                <div className="userTotal">
                                                    <p>{t('total-storage')}</p>
                                                    <div className="total-color all-size">
                                                        <h3>{summary.totalSize?.value}</h3>
                                                        {summary.totalSize?.unit && <h4>{summary.totalSize?.unit}</h4>}
                                                    </div>

                                                </div>
                                                <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#e3e3e3"><path d="M160-280h640v-240H160v240Zm520-60q25 0 42.5-17.5T740-400q0-25-17.5-42.5T680-460q-25 0-42.5 17.5T620-400q0 25 17.5 42.5T680-340Zm200-260H767l-80-80H273l-80 80H80l137-137q11-11 25.5-17t30.5-6h414q16 0 30.5 6t25.5 17l137 137ZM160-200q-33 0-56.5-23.5T80-280v-320h800v320q0 33-23.5 56.5T800-200H160Z" /></svg>

                                            </div>
                                            <div className={`total ${isClickable ? "clickable" : "disabled"}`}
                                                onClick={isClickable ? () => navigateDocManagement() : undefined}
                                            >

                                                <div className="userTotal">
                                                    <p>{t('document count')}</p>
                                                    <div className="total-color all-files">
                                                        <h3>{summary.totalFiles} </h3>
                                                        <h4>{t('files')}</h4>
                                                    </div>

                                                </div>
                                                <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#e3e3e3"><path d="M120-120q-33 0-56.5-23.5T40-200v-480q0-17 11.5-28.5T80-720q17 0 28.5 11.5T120-680v480h640q17 0 28.5 11.5T800-160q0 17-11.5 28.5T760-120H120Zm160-160q-33 0-56.5-23.5T200-360v-440q0-33 23.5-56.5T280-880h167q16 0 30.5 6t25.5 17l57 57h280q33 0 56.5 23.5T920-720v360q0 33-23.5 56.5T840-280H280Zm0-80h560v-360H527l-80-80H280v440Zm0 0v-440 440Z" /></svg>

                                            </div>
                                        </div>


                                        <div className="overview">
                                            <div className="total not-cursor">
                                                <div className="userTotal">
                                                    <p>{t('total-words')}</p>
                                                    <div className="total-color all-words">
                                                        <h3>{formatted_totalWords.number}</h3>
                                                        {formatted_totalWords.unit && <h4>{formatted_totalWords.unit}</h4>}
                                                    </div>

                                                </div>

                                            </div>

                                            <div className="total not-cursor">
                                                <div className="userTotal">
                                                    <p>{t('total-tokens')}</p>
                                                    <div className="total-color all-tokens">
                                                        <h3>{formatted_totalTokens.number}</h3>
                                                        {formatted_totalTokens.unit && <h4>{formatted_totalTokens.unit}</h4>}
                                                    </div>

                                                </div>

                                            </div>

                                        </div>
                                    </div>

                                    <div className="user-overview">
                                        <h2>{t('user-file-overview')}</h2>
                                        <div className="total">
                                            <div className={`user-contain  ${isClickable ? "clickable" : "disabled"}`}
                                                onClick={isClickable ? () => navigateUserManagement() : undefined}>
                                                <div className="userTotal">
                                                    <p>{t('total-user')}</p>
                                                    <div className="total-color all-users">
                                                        <h3> {summary.totalUsers}</h3>
                                                        <h4> {t('users')}</h4>
                                                    </div>

                                                </div>
                                                <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#e3e3e3"><path d="M40-240q-17 0-28.5-11.5T0-280v-23q0-43 44-70t116-27q13 0 25 .5t23 2.5q-14 21-21 44t-7 48v65H40Zm240 0q-17 0-28.5-11.5T240-280v-25q0-32 17.5-58.5T307-410q32-20 76.5-30t96.5-10q53 0 97.5 10t76.5 30q32 20 49 46.5t17 58.5v25q0 17-11.5 28.5T680-240H280Zm500 0v-65q0-26-6.5-49T754-397q11-2 22.5-2.5t23.5-.5q72 0 116 26.5t44 70.5v23q0 17-11.5 28.5T920-240H780Zm-455-80h311q-10-20-55.5-35T480-370q-55 0-100.5 15T325-320ZM160-440q-33 0-56.5-23.5T80-520q0-34 23.5-57t56.5-23q34 0 57 23t23 57q0 33-23 56.5T160-440Zm640 0q-33 0-56.5-23.5T720-520q0-34 23.5-57t56.5-23q34 0 57 23t23 57q0 33-23 56.5T800-440Zm-320-40q-50 0-85-35t-35-85q0-51 35-85.5t85-34.5q51 0 85.5 34.5T600-600q0 50-34.5 85T480-480Zm0-80q17 0 28.5-11.5T520-600q0-17-11.5-28.5T480-640q-17 0-28.5 11.5T440-600q0 17 11.5 28.5T480-560Zm1 240Zm-1-280Z" /></svg>

                                            </div>
                                            <div className={`user-contain  ${isClickable ? "clickable" : "disabled"}`}
                                                onClick={isClickable ? () => navigateUserManagement() : undefined}>
                                                <div className="userTotal">
                                                    <p>{t('total-entity')}</p>
                                                    <div className="total-color">
                                                        <h3> {summary.totalUniqueEntities}</h3>
                                                        <h4> {t('entity')}</h4>
                                                    </div>

                                                </div>

                                            </div>
                                        </div>

                                    </div>
                                </div>

                            </div>

                            <div className="recently">
                                <div className="rec-head">
                                    <h2>{t('contribute-history')}</h2>
                                </div>

                                {/* <div className="all-user" onClick={() =>
                                    handleDetails(user.username)
                                }>
                                    {users.slice(0, 10).map((user) => (
                                        <div className="user-card" key={user.id}>
                                            <div className="profile-avatar">
                                                <ProfileAvatar user={user} />
                                                <div className="userMailDate">
                                                    <span className="email">{user.email}</span>
                                                    <span className="created">
                                                        {i18n.language === "km"
                                                            ? formatDateKhmer(user.createdAt)
                                                            : new Date(user.createdAt).toLocaleString(i18n.language, {
                                                                year: "numeric",
                                                                month: "long",
                                                                day: "numeric",
                                                                hour: "2-digit",
                                                                minute: "2-digit",
                                                            })}
                                                    </span>
                                                </div>
                                    </div>


                                    <div className="view">
                                        <button type="button" onClick={() =>
                                            handleDetails(user.username)
                                        }>
                                            {t('view')}
                                        </button>
                                    </div>

                                     </div>
                                    ))} */}
                                <div className="stats-board">
                                    <table>
                                        {/* Table Header */}
                                        <thead>
                                            <tr>
                                                <th>{t('date')}</th>
                                                <th>{t('files')}</th>
                                                <th>{t('file-size')}</th>
                                                <th>{t('status')}</th>
                                            </tr>
                                        </thead>

                                        {/* Table Body */}
                                        <tbody>
                                            {stats.length > 0 ? (
                                                stats.map((item, index) => (
                                                    <tr key={index}>
                                                        <td>{item.date}</td>
                                                        <td>{item.totalFiles} {t('files')}</td>
                                                        <td>
                                                            {item.size.value + ' ' + item.size.unit}

                                                        </td>
                                                        <td>
                                                            <div className={`status ${item.status}`}>
                                                                {t(item.status)}
                                                            </div></td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="4" style={{ textAlign: 'center', color: '#888', padding: '12px 0' }}>
                                                        {t('no-file-found')}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>


                            </div>
                        </div>
                    </div>
                </div>
            </div></div >

    );
}